import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError, api } from '../../api/client';
import { IconCheckCircle, IconInfo } from '../../shared/icons';
import { AuthLayout } from './AuthLayout';

export function ForgotPasswordPage({ onGoToSignIn }: { onGoToSignIn: () => void }) {
  const [email, setEmail] = useState('');

  const request = useMutation({
    mutationFn: () => api.forgotPassword(email),
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!email.trim()) return;
    request.mutate();
  };

  // The server answers the same way whether or not the address exists, and so
  // does this screen: saying "no such account" here would hand anyone a way to
  // test which addresses are registered.
  if (request.isSuccess) {
    return (
      <AuthLayout kicker="Password reset" heading="Check your inbox">
        <div className="auth-card">
          <div className="notice notice-ok" role="status">
            <IconCheckCircle size={14} />
            <span>
              If <strong>{email}</strong> belongs to an account, a link is on its way. It expires in
              an hour and works once.
            </span>
          </div>
          <p className="field-help">
            Nothing arrived? Check the spam folder. If the account is not active yet, a reset link
            is not sent at all.
          </p>
          <button className="btn btn-block" onClick={onGoToSignIn}>
            Back to sign in
          </button>
        </div>
      </AuthLayout>
    );
  }

  const error = request.error instanceof ApiError ? request.error : null;

  return (
    <AuthLayout kicker="Password reset" heading="Forgot your password?">
      <form className="auth-card" onSubmit={handleSubmit} noValidate>
        {error && (
          <div className="notice notice-error" role="alert">
            <IconInfo size={14} />
            <span>{error.message}</span>
          </div>
        )}

        <p className="field-help">
          Enter the address you sign in with and we will send a link for choosing a new password.
        </p>

        <div className="field">
          <label className="lbl" htmlFor="forgot-email">
            <span className="lbl-text">Work email</span>
          </label>
          <input
            id="forgot-email"
            className="inp"
            type="email"
            autoComplete="username"
            required
            autoFocus
            placeholder="name@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={request.isPending}>
          {request.isPending ? 'Sending…' : 'Send reset link'}
        </button>

        <button className="btn btn-block" type="button" onClick={onGoToSignIn}>
          Back to sign in
        </button>
      </form>
    </AuthLayout>
  );
}
